// search-params.ts — parse query của trang đặt lịch công khai. ?ma= là mã
// người giới thiệu (khuôn cũ), utm_* là tham số đo kênh (spec 2026-08-20),
// ?sdt= là số điện thoại điền sẵn khi khách bấm sang từ cổng khách.
// PHẢI parse đủ khoá trong validateSearch — TanStack Router chuẩn hoá URL
// theo kết quả này, khoá không parse sẽ rụng khỏi search state.
import type { PublicUtm } from "./api";

export type BookingSearch = {
  ma?: string | undefined;
  affDauMay?: string | undefined;
  /** Số điện thoại điền sẵn (?sdt=) — KHÔNG bỏ qua bước nào của form. */
  sdt?: string | undefined;
  utm_source?: string | undefined;
  utm_medium?: string | undefined;
  utm_campaign?: string | undefined;
  utm_content?: string | undefined;
  utm_term?: string | undefined;
};

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/**
 * Router ép giá trị query trông-như-số thành number (và "true"/"false" thành
 * boolean) TRƯỚC khi tới đây, nên chỉ nhận typeof "string" là mất dữ liệu im
 * lặng: chiến dịch đặt tên "1111"/"2027" hay mã giới thiệu toàn số sẽ rụng mà
 * không ai thấy — link preview vẫn đủ, chỉ báo cáo trống. Nhận cả 3 kiểu
 * nguyên thuỷ rồi tự đưa về chuỗi.
 */
function asParam(v: unknown): string | undefined {
  if (typeof v !== "string" && typeof v !== "number" && typeof v !== "boolean") return undefined;
  const s = String(v).trim();
  return s === "" ? undefined : s.slice(0, 200);
}

export function parseBookingSearch(search: Record<string, unknown>): BookingSearch {
  const out: BookingSearch = {};
  const ma = asParam(search.ma) ?? asParam(search.aff);
  if (ma !== undefined) out.ma = ma;
  const affDauMay =
    asParam(search.affDauMay) ?? asParam(search.dauMay) ?? asParam(search.dau_may) ?? asParam(search.dau);
  if (affDauMay !== undefined) out.affDauMay = affDauMay;
  const sdt = asParam(search.sdt);
  if (sdt !== undefined) out.sdt = sdt;
  for (const key of UTM_KEYS) {
    const v = asParam(search[key]);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

/** Gom khoá utm_ thành payload camelCase cho POST /public/customer-bookings. */
export function utmFromSearch(s: BookingSearch): PublicUtm | undefined {
  const utm: PublicUtm = {
    ...(s.utm_source ? { utmSource: s.utm_source } : {}),
    ...(s.utm_medium ? { utmMedium: s.utm_medium } : {}),
    ...(s.utm_campaign ? { utmCampaign: s.utm_campaign } : {}),
    ...(s.utm_content ? { utmContent: s.utm_content } : {}),
    ...(s.utm_term ? { utmTerm: s.utm_term } : {}),
  };
  return Object.keys(utm).length > 0 ? utm : undefined;
}

/**
 * Đổi số điện thoại dạng cổng khách sang dạng ô nhập của form đặt lịch.
 *
 * Phiên cổng khách giữ số dạng chuẩn hoá "84901328854"; form đặt lịch tách
 * đôi — mã vùng "+84" nằm ở ô xổ riêng, ô nhập chỉ giữ phần nội địa
 * "0901328854". Nhét thẳng số dạng 84 vào ô là đơn mang số sai mà nhìn màn
 * hình vẫn thấy có số.
 *
 * Rào `length >= 11`: chuỗi ngắn bắt đầu bằng "84" không phải số Việt dạng
 * quốc tế, cắt đầu là đẻ ra số rác.
 *
 * Cùng luật với hai bản chép riêng đang chạy ở màn nhân viên
 * (`features/customers/CustomersListView.tsx`,
 * `features/customers/CustomerOrderHistoryView.tsx`) và với
 * `phone.NormalizeLocal` bên máy chủ. Cố ý KHÔNG gom chung trong lần sửa này
 * để không đụng vào hai màn đang chạy đúng.
 */
export function localPhoneFromNormalized(normalized: string): string {
  const digits = normalized.replace(/\D/g, "");
  if (digits.startsWith("84") && digits.length >= 11) return `0${digits.slice(2)}`;
  return digits;
}
